import { useState } from 'react';
import { photoAPI } from '../services/api';
import { getUploadUrl } from '../utils/config';
import './PhotoViewer.css';

function PhotoViewer({ photo, members, onClose, onDelete }) {
  const [tags, setTags] = useState(photo.tags || []);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [addingTag, setAddingTag] = useState(false);

  const handleAddTag = async () => {
    if (!selectedMemberId) return;

    setAddingTag(true);
    try {
      await photoAPI.addTag(photo.id, parseInt(selectedMemberId));

      // Refresh tags by fetching photo details again
      const response = await photoAPI.getById(photo.id);
      setTags(response.data.tags || []);
      setSelectedMemberId('');
    } catch (error) {
      console.error('Error adding tag:', error);
      alert(error.response?.data?.error || 'Failed to add tag');
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await photoAPI.removeTag(photo.id, tagId);
      setTags(tags.filter(t => t.id !== tagId));
    } catch (error) {
      console.error('Error removing tag:', error);
      alert('Failed to remove tag');
    }
  };

  const availableMembers = members.filter(
    m => !tags.some(t => t.member_id === m.id)
  );

  return (
    <div className="photo-viewer-overlay" onClick={onClose}>
      <div className="photo-viewer-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>

        <div className="photo-viewer-content">
          <div className="photo-viewer-image">
            <img src={getUploadUrl(photo.file_path)} alt={photo.caption || 'Photo'} />
          </div>

          <div className="photo-viewer-sidebar">
            <div className="photo-metadata">
              <h3>Photo Details</h3>

              {photo.original_filename && (
                <div className="metadata-item">
                  <label>Filename:</label>
                  <span>{photo.original_filename}</span>
                </div>
              )}

              {photo.photo_date && (
                <div className="metadata-item">
                  <label>Date:</label>
                  <span>{new Date(photo.photo_date).toLocaleString()}</span>
                </div>
              )}

              {photo.caption && (
                <div className="metadata-item">
                  <label>Caption:</label>
                  <span>{photo.caption}</span>
                </div>
              )}

              {photo.exif_data && photo.exif_data.Make && (
                <div className="metadata-item">
                  <label>Camera:</label>
                  <span>{photo.exif_data.Make} {photo.exif_data.Model}</span>
                </div>
              )}
            </div>

            <div className="photo-tags-section">
              <h3>Tagged People</h3>

              <div className="current-tags">
                {tags.length === 0 ? (
                  <p className="no-tags">No one tagged yet</p>
                ) : (
                  tags.map((tag) => (
                    <div key={tag.id} className="tag-item">
                      <div className="tag-info">
                        <span className="tag-name">
                          {tag.first_name} {tag.last_name}
                        </span>
                        <span className="tag-meta">
                          Tagged by {tag.tagged_by_username}
                        </span>
                      </div>
                      <button
                        className="remove-tag-btn"
                        onClick={() => handleRemoveTag(tag.id)}
                        title="Remove tag"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="add-tag-section">
                <select
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                  disabled={addingTag || availableMembers.length === 0}
                >
                  <option value="">Select person to tag...</option>
                  {availableMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </option>
                  ))}
                </select>
                <button
                  className="add-tag-btn"
                  onClick={handleAddTag}
                  disabled={!selectedMemberId || addingTag}
                >
                  {addingTag ? 'Adding...' : 'Add Tag'}
                </button>
              </div>
            </div>

            <div className="photo-actions">
              <button className="delete-photo-btn" onClick={onDelete}>
                Delete Photo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhotoViewer;
